import type {
  BibCut,
  BodyShape,
  Cut,
  CutScores,
  PainPoint,
  QuizAnswers,
  QuizResult,
} from '../types/quiz'

/**
 * Pain-point signal weights.
 *
 * Bottom-heavy signals (waist gap, tight thighs, hip-driven sizing, crotch rips)
 * lean Zali. Mid-section signals (waist digs, buttons popping, baggy legs) lean
 * Beatrice first, Mila second. Universal signals ("length is always wrong",
 * "wears men's pants", "none") don't discriminate between cuts - they're for
 * validation copy on the result screen, not scoring.
 *
 * Pain-point totals cap at ~3 points per selection, so body shape (5 points)
 * is always the strongest single signal and pains fine-tune on top.
 */
export const PAIN_WEIGHTS: Record<PainPoint, CutScores> = {
  // Bottom-heavy (Zali-weighted)
  waist_gap:     { zali: 3, mila: 0, beatrice: 0 },
  tight_thighs:  { zali: 3, mila: 0, beatrice: 0 },
  sized_up_hips: { zali: 3, mila: 0, beatrice: 0 },
  crotch_rips:   { zali: 2, mila: 1, beatrice: 1 },

  // Mid-section (Mila / Beatrice-weighted)
  waist_digs:    { zali: 0, mila: 2, beatrice: 3 },
  buttons_pop:   { zali: 0, mila: 2, beatrice: 3 },
  baggy_legs:    { zali: 0, mila: 2, beatrice: 3 },

  // Universal (validation only - no scoring signal)
  length_wrong:  { zali: 0, mila: 0, beatrice: 0 },
  wears_mens:    { zali: 1, mila: 1, beatrice: 1 },
  none:          { zali: 0, mila: 0, beatrice: 0 },
}

/**
 * Body-shape weights. Heaviest single signal (5 points).
 *
 * Apple and Rectangle both route to Beatrice - the Beatrice cut covers the
 * "similar waist-to-hip ratio / extra mid-section room" use case regardless of
 * whether the customer self-identifies as apple or rectangle.
 */
export const SHAPE_WEIGHTS: Record<BodyShape, CutScores> = {
  pear:         { zali: 5, mila: 0, beatrice: 0 },
  subtle_curve: { zali: 0, mila: 5, beatrice: 0 },
  apple:        { zali: 0, mila: 0, beatrice: 5 },
  rectangle:    { zali: 0, mila: 0, beatrice: 5 },
}

const CUTS_IN_PRIORITY_ORDER: readonly Cut[] = ['zali', 'mila', 'beatrice'] as const

const EMPTY_SCORES = (): CutScores => ({ zali: 0, mila: 0, beatrice: 0 })

/**
 * Compute raw scores from a quiz answer set. Pure function - no fallbacks
 * applied. Exported so the dashboard and tests can inspect the scoring
 * surface independently of the override rules.
 */
export function computeScores(answers: QuizAnswers): CutScores {
  const scores = EMPTY_SCORES()

  for (const pain of answers.painPoints) {
    const w = PAIN_WEIGHTS[pain]
    scores.zali     += w.zali
    scores.mila     += w.mila
    scores.beatrice += w.beatrice
  }

  const s = SHAPE_WEIGHTS[answers.bodyShape]
  scores.zali     += s.zali
  scores.mila     += s.mila
  scores.beatrice += s.beatrice

  return scores
}

/**
 * Pick a raw winner from scores. Deterministic tie-break:
 *   1. Highest total score wins.
 *   2. On a tie, prefer the cut favoured by the user's body shape
 *      (so a tied "rectangle" user leans Beatrice over Zali).
 *   3. Final fallback: declared order zali > mila > beatrice.
 */
export function pickWinner(scores: CutScores, bodyShape: BodyShape): Cut {
  const shape = SHAPE_WEIGHTS[bodyShape]

  return CUTS_IN_PRIORITY_ORDER.reduce<Cut>((best, cut) => {
    if (scores[cut] > scores[best]) return cut
    if (scores[cut] < scores[best]) return best
    // Tie on raw score - prefer the cut the shape signal points at.
    if (shape[cut] > shape[best]) return cut
    return best
  }, CUTS_IN_PRIORITY_ORDER[0])
}

/**
 * Main scoring entry point. Applies hard fallback rules AFTER scoring:
 *
 *   1. Beatrice + tall height -> keep Beatrice cut, drop length to Regular.
 *      (Beatrice is only cut in Short/Regular - no Tall inseam exists.)
 *      The shape match is more important than a perfect inseam, so we keep
 *      the right cut and let the UI surface a "prefer length over fit?" link
 *      to the Mila tall as an alternative.
 *
 *   2. Beatrice winner bib request -> bib_cut = 'mila'.
 *      (Beatrice has no bib product.)
 *
 *   3. Any override flips `fallback_triggered = true` so analytics can
 *      measure how often the Beatrice range constrains us.
 */
export function scoreQuiz(answers: QuizAnswers): QuizResult {
  const scores = computeScores(answers)
  const rawWinner = pickWinner(scores, answers.bodyShape)

  const recommended_cut: Cut = rawWinner
  let recommended_length: typeof answers.heightBand = answers.heightBand
  let fallback_triggered = false

  // Rule 1 - Beatrice has no Tall length. Stay on Beatrice, drop to Regular.
  if (rawWinner === 'beatrice' && answers.heightBand === 'tall') {
    recommended_length = 'regular'
    fallback_triggered = true
  }

  // Rule 2 - Beatrice has no bib. Flip bib cut to Mila; also trigger the flag.
  const bib_cut: BibCut = rawWinner === 'beatrice' ? 'mila' : rawWinner
  if (rawWinner === 'beatrice') fallback_triggered = true

  return {
    recommended_cut,
    recommended_length,
    // Bib length always reflects the user's actual height - all bib cuts ship
    // in every length, so we don't need the Beatrice-tall workaround here.
    bib_length: answers.heightBand,
    bib_cut,
    // bib_available reflects the RAW winner - false only when the primary cut
    // itself has no bib product. The UI still shows a bib (the Mila fallback),
    // but this flag lets the copy distinguish "your cut's bib" vs fallback.
    bib_available: rawWinner !== 'beatrice',
    fallback_triggered,
    scores,
  }
}
